import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode, RefObject } from 'react';

/** A dialog for a form or a choice. Destructive confirmations use ConfirmDialog instead. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  initialFocus,
  returnFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** Takes focus on open; without it, or while it is empty, the first control in the dialog does. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Takes focus on close, for a dialog opened from code with no trigger to return to; the page's main region does if it is gone. */
  returnFocus?: RefObject<HTMLElement | null>;
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="rq-dialog-backdrop">
          <RadixDialog.Content
            className="rq-dialog rq-dialog--form"
            onOpenAutoFocus={
              initialFocus
                ? (event) => {
                    if (!initialFocus.current) return;
                    event.preventDefault();
                    initialFocus.current.focus();
                  }
                : undefined
            }
            onCloseAutoFocus={
              returnFocus
                ? (event) => {
                    event.preventDefault();
                    if (returnFocus.current?.isConnected) returnFocus.current.focus();
                    else document.getElementById('main')?.focus();
                  }
                : undefined
            }
            {...(description ? {} : { 'aria-describedby': undefined })}
          >
            <RadixDialog.Title className="rq-dialog-title text-heading">{title}</RadixDialog.Title>
            {description ? (
              <RadixDialog.Description className="rq-dialog-desc">
                {description}
              </RadixDialog.Description>
            ) : null}
            <div className="rq-dialog-body">{children}</div>
            {footer ? <div className="rq-dialog-foot">{footer}</div> : null}
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
